import { ApolloError, useMutation } from '@apollo/client';
import {
  DeleteDocumentError,
  DeleteDocumentModal,
  ImageViewer,
  MilestoneStatusMessages,
  useAppUser,
  useDate,
  useImageViewerState,
  useName,
} from '@bechtel/client-common';
import { DELETE_WELD_ATTACHMENT, GET_WELD_ATTACHMENTS, GET_WELD_PHOTOS } from '@bechtel/client-gql';
import { Avatar, AvatarSize, Box, Icon, IconSize, Text, ToastAction, ToastType, useToastsDispatch } from '@bechtel/gds';
import {
  Attachment,
  AttachmentType,
  DeleteWeldAttachmentError,
  MilestoneStatusCode
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, map } from 'lodash';
import { FC, Fragment, useState } from 'react';
import { ImageBoxStyled, ImageStyled } from '../styled';

interface ImageGalleryProps {
  attachments: Attachment[];
  milestoneStatus: MilestoneStatusCode;
  commodityId: string;
  canDeleteCommodityAttachment: boolean;
  canDeleteWeldAttachment?: boolean; // New permission for weld photos
  onPhotoDeleted?: () => void;
}

export const WeldPhotoList: FC<ImageGalleryProps> = ({
  attachments,
  milestoneStatus,
  commodityId,
  canDeleteWeldAttachment,
  onPhotoDeleted,
}) => {
  const [imagePosition, setImagePosition] = useState(0);
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const images = attachments && attachments.length > 0 ? map(attachments, (attachment) => attachment?.url) : [];
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const { formatISODate } = useDate();
  const { user } = useAppUser();
  const dispatchToast = useToastsDispatch();

  // Delete photo state
  const [photoToDelete, setPhotoToDelete] = useState<Attachment | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<DeleteWeldAttachmentError | ApolloError | null>(null);
  const setGalleryImagePosition = (index) => {
    setImagePosition(index);
  };
  // Delete photo mutation
  const [deleteWeldAttachment, { loading: deleteLoading }] = useMutation(DELETE_WELD_ATTACHMENT, {
    refetchQueries: [
      {
        query: GET_WELD_PHOTOS,
        variables: {
          ids: [commodityId],
          paging: {
            cursor: '1',
            limit: 10,
          },
        },
      },
      {
        query: GET_WELD_ATTACHMENTS,
        variables: {
          ids: [commodityId],
        },
      },
    ],
    onCompleted: (data) => {
      const error = data?.deleteWeldAttachment?.error;
      if (!error) {
        // Success toast
        const successToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.SUCCESS,
            content: t`Photo deleted successfully`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(successToast);

        // Callback to parent to update photo count
        if (onPhotoDeleted) {
          onPhotoDeleted();
        }
      }
      // Set error for modal display
      setDeleteError(error);
      setShowDeleteConfirm(false);
      setPhotoToDelete(null);
    },
    onError: (apolloError) => {
      setDeleteError(apolloError);
      setShowDeleteConfirm(false);
      setPhotoToDelete(null);
    },
  });

  const handleDeletePhoto = (attachment: Attachment) => {
    setPhotoToDelete(attachment);
    setShowDeleteConfirm(true);
  };
  const confirmDeletePhoto = () => {
    if (photoToDelete) {
      deleteWeldAttachment({
        variables: {
          input: {
            weldId: commodityId,
            attachmentId: photoToDelete.id,
          },
        },
      });
    }
  };

  const cancelDeletePhoto = () => {
    setShowDeleteConfirm(false);
    setPhotoToDelete(null);
  };

  const closeErrorModal = () => {
    setDeleteError(null);
  };

  // Check if current user can delete a photo
  const canDeletePhoto = (attachment: Attachment) => {
    // Use weld-specific permission
    return canDeleteWeldAttachment && attachment?.creator?.id === user?.id;
  };

  return (
    <Fragment>
      {visibleImageViewer && (
        <ImageViewer
          images={images}
          selectedImageIndex={imagePosition}
          visible={visibleImageViewer}
          closable={true}
          onDismiss={closeImageViewer}
        />
      )}

      {showDeleteConfirm && (
        <DeleteDocumentModal
          title={t`Delete Photo`}
          textContent={t`Are you sure you want to delete this photo?`}
          onCancel={cancelDeletePhoto}
          onDelete={confirmDeletePhoto}
        />
      )}

      {deleteError && (
        <DeleteDocumentError error={deleteError} onClose={closeErrorModal} />
      )}

      <Box data-testid="weld-card-image" mb={{ sm: '500' }}>
        <Box
          data-testid="weld-image-viewer"
          style={{ flexFlow: 'row wrap' }}
          flexDirection={{ sm: 'column' }}
          flexGrow={{ sm: 1 }}
          justifyContent={{ sm: 'space-between' }}
        >
          {attachments &&
            attachments.length &&
            map(
              filter(attachments, (attachment) => attachment?.attachmentType == AttachmentType.IMAGE),
              (file, index: number) => {
                return (
                  <ImageBoxStyled key={file.id}>
                    <Box style={{ position: 'relative' }}>
                      <ImageStyled
                        src={file['url']}
                        onClick={(e) => {
                          setGalleryImagePosition(index);
                          e.stopPropagation();
                          openImageViewer();
                        }}
                      />
                      {/* Delete button - only show if user can delete this photo */}
                      {canDeletePhoto(file) && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '4px',
                            padding: '4px',
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePhoto(file);
                          }}
                        >
                          <Icon icon="trash-can" size={IconSize.SMALL} fillColor="white" />
                        </Box>
                      )}
                    </Box>
                    {file?.creator?.id && (
                      <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }} p={{ sm: '400' }}>
                        <Box>
                          <Avatar
                            size={AvatarSize.MEDIUM}
                            src={file?.creator?.imageUrl ? file.creator.imageUrl : undefined}
                          />
                          <Box flexDirection={{ sm: 'column' }} ml={{ sm: '400' }} justifyContent={{ sm: 'center' }}>
                            <Text size="300" color="grey.1000" weight="600" family="graphikCompact">
                              {getFullName(file?.creator?.firstName, file?.creator?.lastName)}
                            </Text>
                          </Box>
                        </Box>
                        <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'flex-end' }}>
                          <Box mb={{ sm: '300' }}>
                            <Text size="300" color="grey.1000" family="graphikCompact">
                              {formatISODate(file?.createdAt)}
                            </Text>
                          </Box>
                          <Text size="300" color="grey.1000" family="graphikCompact">
                            <Trans>Status</Trans>: {milestoneStatus && i18n._(MilestoneStatusMessages[milestoneStatus])}
                          </Text>
                        </Box>
                      </Box>
                    )}
                  </ImageBoxStyled>
                );
              }
            )}
        </Box>
      </Box>
    </Fragment>
  );
};
